import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  domainRouterClient,
  type DomainRouterMutationArgs,
  type DomainRouterMutationData,
  type DomainRouterMutationName,
  type DomainRouterQueryArgs,
  type DomainRouterQueryData,
  type DomainRouterQueryName,
} from '../api/domainRouterClient';
import {
  DOMAIN_CONTEXT_TAGS,
  resolveMutationContext,
  resolveQueryContext,
  type DomainContextTag,
} from './domainRouterOperationRegistry';
import { toDomainRouterApiError } from './domainRouterError';
import { YSTNJ_DOMAIN_ROUTER_CACHE_RETENTION_SECONDS } from './domainRouterCachePolicy';

type ApiError = {
  message: string;
};

type CacheKeyValue = string | number | boolean | null | CacheKeyValue[] | { [key: string]: CacheKeyValue };

type DomainQueryRequest = {
  [Name in DomainRouterQueryName]: {
    operation: Name;
    args: DomainRouterQueryArgs<Name>;
  };
}[DomainRouterQueryName];

type DomainMutationRequest = {
  [Name in DomainRouterMutationName]: {
    operation: Name;
    args: DomainRouterMutationArgs<Name>;
  };
}[DomainRouterMutationName];

type DomainQueryUnionData = DomainRouterQueryData<DomainRouterQueryName>;
type DomainMutationUnionData = DomainRouterMutationData<DomainRouterMutationName>;

type DomainTagType = DomainContextTag | 'DomainOperation';

const asError = (error: unknown): ApiError => toDomainRouterApiError(error);

const toCacheKeyValue = (value: unknown): CacheKeyValue => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    const normalized = value.map((entry) => toCacheKeyValue(entry));
    const allPrimitive = normalized.every(
      (entry) => entry == null || typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean'
    );
    if (allPrimitive) {
      return [...normalized].sort((left, right) => String(left).localeCompare(String(right)));
    }
    return normalized;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry != null && !(typeof entry === 'string' && entry.trim() === ''))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, toCacheKeyValue(entry)] as const);
    return Object.fromEntries(entries);
  }
  return String(value);
};

const buildCacheKey = (scope: string, value: unknown): string =>
  `${scope}:${JSON.stringify(toCacheKeyValue(value))}`;

const operationCacheKey = (
  operationType: 'query' | 'mutation',
  operation: string,
  args: unknown
): string =>
  buildCacheKey(operationType, {
    operation,
    args,
  });

const buildQueryTags = (request: DomainQueryRequest): Array<{ type: DomainTagType; id: string }> => {
  const domainType = resolveQueryContext(request.operation);
  const operationId = operationCacheKey('query', request.operation, request.args);
  return [
    { type: 'DomainOperation', id: operationId },
    { type: domainType, id: 'LIST' },
    { type: domainType, id: operationId },
  ];
};

const buildMutationTags = (request: DomainMutationRequest): Array<{ type: DomainTagType; id: string }> => {
  const domainType = resolveMutationContext(request.operation);
  const operationId = operationCacheKey('mutation', request.operation, request.args);
  return [
    { type: 'DomainOperation', id: operationId },
    { type: domainType, id: 'LIST' },
    { type: domainType, id: operationId },
  ];
};

export const domainRouterUniversalApi = createApi({
  reducerPath: 'domainRouterUniversalApi',
  baseQuery: fakeBaseQuery<ApiError>(),
  keepUnusedDataFor: YSTNJ_DOMAIN_ROUTER_CACHE_RETENTION_SECONDS,
  serializeQueryArgs: ({ endpointName, queryArgs }) => buildCacheKey(endpointName, queryArgs),
  tagTypes: ['DomainOperation', ...DOMAIN_CONTEXT_TAGS],
  endpoints: (builder) => ({
    domainQuery: builder.query<DomainQueryUnionData, DomainQueryRequest>({
      queryFn: async (request) => {
        try {
          const data = await domainRouterClient.query(
            request.operation as DomainRouterQueryName,
            request.args as DomainRouterQueryArgs<DomainRouterQueryName>
          );
          return { data: data as DomainQueryUnionData };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, request) => buildQueryTags(request),
    }),

    domainMutation: builder.mutation<DomainMutationUnionData, DomainMutationRequest>({
      queryFn: async (request) => {
        try {
          const data = await domainRouterClient.mutate(
            request.operation as DomainRouterMutationName,
            request.args as DomainRouterMutationArgs<DomainRouterMutationName>
          );
          return { data: data as DomainMutationUnionData };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        // Default optimistic behavior: pre-invalidate domain-scoped tags so subscribers refresh immediately.
        dispatch(domainRouterUniversalApi.util.invalidateTags(buildMutationTags(request)));
        try {
          await queryFulfilled;
        } catch {
          // Keep request-scoped invalidation optimistic by default; no rollback is required for tag invalidation.
        }
      },
      invalidatesTags: (_result, _error, request) => buildMutationTags(request),
    }),
  }),
});

export type { DomainMutationRequest, DomainQueryRequest };

export const {
  useDomainQueryQuery,
  useLazyDomainQueryQuery,
  useDomainMutationMutation,
} = domainRouterUniversalApi;
